import React from 'react';
import { useFormikContext } from 'formik';

import { ShareEmailCapture } from '../../form/fields/email-capture';
import { ShareExpirationDatePicker } from '../../form/fields/expiration-date';
import { ShareLinkFieldType, ShareLinkFormType } from '../../form/link-config-form.factory';
import { SharePasswordProtect } from '../../form/fields/password-protect';
import { ShareNda } from '../../form/fields/nda';
import { SharePanelFormCustomFieldsSectionLink } from '../../form/sections/custom-fields.section';
import { useSharePanelStore } from 'src/common/store/share.store';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { Button } from 'src/common/button';

export const ShareConfigAccessPanel = () => {
  const { values, setFieldValue } = useFormikContext<ShareLinkFormType>();

  const onFieldChange = (payload: ShareLinkFieldType<unknown>) => setFieldValue(payload.field, payload.value);

  const onJourneyShareClose = useSharePanelStore((state) => state.onJourneyShareClose);

  const closePanel = () => {
    onJourneyShareClose();
  };

  const { subscriptionPlanType, isOnTrial } = useFeatureRestrictions();

  // Allow advanced access permissions for Grow (pro), Scale (enterprise), and trial plans
  const isRestricted = subscriptionPlanType === 'starter' && !isOnTrial;

  return (
    <>
      <div className='flex flex-col divide-y'>
        {/* Always allowed features */}
        <ShareNda
          ndaRequired={{ field: 'nda_required', value: values.nda_required }}
          ndaLegalEntityName={{ field: 'nda_legal_entity_name', value: values.nda_legal_entity_name || '' }}
          customNdaTemplateUrl={{ field: 'custom_nda_template_url', value: values.custom_nda_template_url || '' }}
          emailRequired={{ field: 'email_required', value: values.email_required }}
          emailVerificationRequired={{ field: 'email_verification', value: values.email_verification }}
          onFieldChange={onFieldChange}
        />
        <SharePasswordProtect
          password={{ field: 'password', value: values.password || '' }}
          onFieldChange={onFieldChange}
        />

        {/* Advanced features, always shown but disabled if restricted */}
        <ShareEmailCapture
          emailRequired={{ field: 'email_required', value: values.email_required }}
          emailVerificationRequired={{ field: 'email_verification', value: values.email_verification }}
          whitelistedEmails={{ field: 'allowed_emails', value: values.allowed_emails || [] }}
          blacklistedEmails={{ field: 'blacklisted_emails', value: values.blacklisted_emails || [] }}
          ndaRequired={{ field: 'nda_required', value: values.nda_required }}
          autoAddWhitelist={{ field: 'auto_add_whitelist', value: values.auto_add_whitelist }}
          onFieldChange={onFieldChange}
          disabled={isRestricted}
        />
        <ShareExpirationDatePicker
          date={{ field: 'expires_at', value: values.expires_at || '' }}
          onFieldChange={onFieldChange}
          disabled={isRestricted}
        />
        <SharePanelFormCustomFieldsSectionLink disabled={isRestricted} />

        {/* Upgrade message and button if restricted */}
        {isRestricted && (
          <div className='py-6 flex flex-col items-start gap-4'>
            <div className='text-bedrock-p-strong mb-2'>Upgrade Required</div>
            <div className='text-bedrock-p text-bedrock-gray-dark mb-2'>
              Upgrade to Grow or Scale plan to access advanced access permissions like NDA, Email Capture, Expiration
              Date, and Custom Fields.
            </div>
            <Button
              label='Upgrade'
              variant='primary'
              size='regular'
              onClick={() => window.location.assign('/billing')}
            />
          </div>
        )}
      </div>
    </>
  );
};
